// src/pages/SendingActivity.jsx
// Users → Sending activity (3.80.0, owner, Nov 4): who sends faxes, by portal
// or by email, and who doesn't. Fax counts only, never pages. Who sees it is
// set in Roles & Access (sending.view; on for business clients' Managers and up).
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import SortTh, { useSort } from '../components/SortTh';
import ShowSelect from '../components/ShowSelect';
import Pager, { usePager } from '../components/Pager';
import EmptyState from '../components/EmptyState';
import SendingWeeks, { sendingTiles } from '../components/SendingWeeks';
import Loader from '../components/Loader';
import { API_BASE } from '../firebase/config';
import { downloadSheet } from '../utils/api';
import { formatDate } from '../utils/format';
import { pathForTab } from '../utils/tabRoutes';
import { PILL, PILL_ON } from '../theme/tabs';
import { timeZone } from '../utils/timeZone';

const STATUS_LOOK = {
  portal: ['var(--c-e0f2f1)', 'var(--t-00695c)'], email: ['var(--c-e3f2fd)', 'var(--t-1565c0)'], both: ['var(--c-f3e5f5)', 'var(--t-6a1b9a)'],
  stopped: ['var(--c-fff3e0)', 'var(--t-e65100)'], inbox: ['var(--c-f1f8e9)', 'var(--t-2e7d32)'],
  signedIn: ['var(--c-eceff1)', 'var(--t-546e7a)'], none: ['var(--c-eceff1)', 'var(--t-546e7a)'], former: ['var(--c-eceff1)', 'var(--t-78909c)'],
};

export default function SendingActivity() {
  const { getToken, can } = useAuth();
  const [days, setDays] = useState(7);   // 3.82.1 (owner): as the Overview
  const [range, setRange] = useState({ from: '', to: '' });   // 3.80.1: a day or dates inside the last 30 days
  const [client, setClient] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [team, setTeam] = useState('');
  const [search, setSearch] = useState('');
  const [show, setShow] = useState('50');

  useEffect(() => {
    let live = true;
    setData(null); setError('');
    (async () => {
      try {
        const q = range.from ? `startDate=${range.from}&endDate=${range.to || range.from}` : `days=${days}`;
        const r = await fetch(`${API_BASE}/portal/logs/sending-activity?${q}`, { headers: { Authorization: `Bearer ${await getToken()}` } });
        const j = await r.json();
        if (!r.ok) throw new Error(j.error || "Couldn't load Sending activity.");
        if (live) setData(j);
      } catch (e) { if (live) { setError(e.message); setData({ people: [], weeks: [], clients: [] }); } }
    })();
    return () => { live = false; };
  }, [days, range.from, range.to, getToken]);

  const people = useMemo(() => data?.people || [], [data]);
  const manyClients = (data?.clients || []).length > 1;
  // 3.80.1: teams of the picked client; on all clients each says whose it is (names can repeat)
  const inClient = useMemo(() => people.filter(p => !client || p.accountId === client), [people, client]);
  const teams = useMemo(() => [...new Map(inClient.flatMap(p => p.teams.map(t => [`${p.accountId}|${t}`, manyClients && !client ? `${t} (${p.accountName})` : t]))).entries()]
    .sort((a, b) => a[1].localeCompare(b[1])), [inClient, manyClients, client]);
  const statuses = useMemo(() => [...new Map(people.map(p => [p.status, p.statusText])).entries()], [people]);
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return inClient.filter(p => (!status || p.status === status) && (!team || p.teams.some(t => `${p.accountId}|${t}` === team))
      && (!q || `${p.name || ''} ${p.email}`.toLowerCase().includes(q)));
  }, [inClient, status, team, search]);
  const { sorted, sort, onSort } = useSort(shown, {
    name: p => p.name || p.email, client: p => p.accountName, role: p => p.role || '', team: p => p.teams.join(', '), portal: p => p.viaPortal, email: p => p.viaEmail,
    total: p => p.total, status: p => p.statusText, last: p => p.lastSent || '', signIn: p => p.lastSignIn || '', opened: p => p.opened,
  });
  const { pageRows, pager } = usePager(sorted, show);

  const only = st => shown.filter(p => p.status === st).length;
  const tiles = sendingTiles({
    sent: shown.filter(p => p.total > 0 && p.status !== 'former').length, people: shown.filter(p => p.status !== 'former').length,
    viaPortal: shown.reduce((n, p) => n + p.viaPortal, 0), viaEmail: shown.reduce((n, p) => n + p.viaEmail, 0),
    portalOnly: only('portal'), emailOnly: only('email'), both: only('both'), stopped: only('stopped'), inboxOnly: only('inbox'),
    notSending: only('inbox') + only('signedIn') + only('none'),
  });
  const weeks = (client && data?.weeksByClient?.[client]) || data?.weeks || [];
  // Dates: from the first day we know how faxes were sent, or 30 days back
  const dayOf = t => new Date(t).toLocaleDateString('en-CA', { timeZone: timeZone() });
  const today = dayOf(Date.now());
  const firstDay = [dayOf(Date.now() - 30 * 86400000), data?.since ? dayOf(data.since) : ''].sort().pop();
  const pick = (from, to) => setRange({ from, to: to && to >= from ? to : from });

  function openFaxes(email) {
    try { window.sessionStorage.setItem('faxPortal.outboxSender', email); } catch { /* the Outbox opens unfiltered */ }
    window.history.pushState({ tab: 'outgoing' }, '', pathForTab('outgoing'));
    window.dispatchEvent(new PopStateEvent('popstate'));
  }
  async function download() {
    await downloadSheet('Sending activity', [
      { header: 'Person', get: p => p.name || p.email, width: 26 },
      { header: 'Email', get: p => p.email, width: 32 },
      ...(manyClients ? [{ header: 'Client', get: p => p.accountName, width: 18 }] : []),
      { header: 'Role', get: p => p.role || '', width: 12 },
      { header: 'Team', get: p => p.teams.join(', '), width: 18 },
      { header: 'Portal faxes', get: p => p.viaPortal, width: 12 },
      { header: 'Email faxes', get: p => p.viaEmail, width: 12 },
      { header: 'Total faxes', get: p => p.total, width: 12 },
      { header: 'Status', get: p => p.statusText, width: 22 },
      { header: 'Last fax sent', get: p => (p.lastSent ? formatDate(p.lastSent) : ''), width: 22 },
      { header: 'Inbox faxes opened', get: p => p.opened, width: 12 },
      { header: 'Last sign-in', get: p => (p.lastSignIn ? formatDate(p.lastSignIn) : ''), width: 22 },
    ], sorted, getToken);
  }

  return (
    <div>
      <div style={S.filterBar}>
        <div style={{ ...S.row, justifyContent: 'space-between' }}>
          <div style={S.row}>
            {[[7, '7 days'], [30, '30 days']].map(([d, l]) => (
              <button key={d} onClick={() => { setRange({ from: '', to: '' }); setDays(d); }} style={{ ...PILL, ...(!range.from && days === d ? PILL_ON : {}) }} aria-pressed={!range.from && days === d}>{l}</button>
            ))}
            <label style={S.dateLabel}>From <input type="date" value={range.from} min={firstDay} max={today} onChange={e => (e.target.value ? pick(e.target.value, range.to) : setRange({ from: '', to: '' }))} style={S.input} /></label>
            <label style={S.dateLabel}>To <input type="date" value={range.to} min={range.from || firstDay} max={today} disabled={!range.from} onChange={e => pick(range.from, e.target.value)} style={S.input} /></label>
            {range.from && <button onClick={() => setRange({ from: '', to: '' })} style={S.clear}>Clear dates</button>}
          </div>
          {can('fax.reports') && <button onClick={download} disabled={!sorted.length} style={{ ...S.download, opacity: sorted.length ? 1 : 0.5 }} title="Download what's shown as an Excel file">⬇ Download report (.xlsx)</button>}
        </div>
        <div style={{ ...S.row, marginTop: 12 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or email" aria-label="Search name or email" style={S.input} />
          {manyClients && <select value={client} onChange={e => { setClient(e.target.value); setTeam(''); }} aria-label="Client" style={S.input}>
            <option value="">All clients</option>
            {data.clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>}
          <select value={status} onChange={e => setStatus(e.target.value)} aria-label="Status" style={S.input}>
            <option value="">All statuses</option>
            {statuses.map(([id, text]) => <option key={id} value={id}>{text}</option>)}
          </select>
          {teams.length > 0 && <select value={team} onChange={e => setTeam(e.target.value)} aria-label="Team" style={S.input}>
            <option value="">All teams</option>
            {teams.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>}
          <ShowSelect value={show} onChange={setShow} style={S.input} groupStyle={{ display: 'flex', alignItems: 'center', gap: 6 }} />
        </div>
        <p style={S.note}>
          Each fax counts once: automatic retries and Resends aren't added, and neither is a fax Fax Portal support sent for someone.
          Inbox faxes opened: how many received faxes this person opened in the portal (viewed or downloaded). A fax they only read in their email isn't counted.
          {data?.since && ` How a fax was sent is known from ${new Date(data.since).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}.`}
        </p>
      </div>
      {error && <div style={S.error}>{error}</div>}
      {data == null ? <Loader /> : <>
        <div style={S.tiles}>
          {tiles.map(([label, value, about]) => (
            <div key={label} style={S.tile}>
              <div style={S.tileValue}>{typeof value === 'number' ? value.toLocaleString() : value}</div>
              <div style={S.tileLabel}>{label}</div>
              <div style={S.tileSub}>{about}</div>
            </div>
          ))}
        </div>
        {weeks.length > 0 && <div style={S.trend}><div style={S.trendHead}><strong>Faxes each week</strong><span style={S.trendHint}>Point at a colour to see that week's numbers</span></div><SendingWeeks weeks={weeks} /></div>}
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr style={S.thead}>
                <SortTh k="name" label="Person" sort={sort} onSort={onSort} style={S.th} />
                {manyClients && <SortTh k="client" label="Client" sort={sort} onSort={onSort} style={S.th} />}
                <SortTh k="role" label="Role" sort={sort} onSort={onSort} style={S.th} />
                {teams.length > 0 && <SortTh k="team" label="Team" sort={sort} onSort={onSort} style={S.th} />}
                <SortTh k="portal" label="Portal faxes" sort={sort} onSort={onSort} style={S.th} />
                <SortTh k="email" label="Email faxes" sort={sort} onSort={onSort} style={S.th} />
                <SortTh k="total" label="Total faxes" sort={sort} onSort={onSort} style={S.th} />
                <SortTh k="status" label="Status" sort={sort} onSort={onSort} style={S.th} />
                <SortTh k="last" label="Last fax sent" sort={sort} onSort={onSort} style={S.th} />
                <SortTh k="opened" label="Inbox faxes opened" sort={sort} onSort={onSort} style={S.th} />
                <SortTh k="signIn" label="Last sign-in" sort={sort} onSort={onSort} style={S.th} />
              </tr>
            </thead>
            <tbody>
              {!pageRows.length ? <tr><td colSpan={11} style={S.empty}><EmptyState icon="📊" emptyText="Nobody matches these filters." /></td></tr>
                : pageRows.map((p, i) => {
                  const clickable = p.total > 0 && can('tab.outgoing');
                  const [bg, color] = STATUS_LOOK[p.status] || STATUS_LOOK.none;
                  return (
                    <tr key={`${p.accountId}|${p.email}`} onClick={clickable ? () => openFaxes(p.email) : undefined} title={clickable ? 'See their faxes in the Outbox' : undefined}
                      style={{ background: i % 2 === 0 ? 'var(--c-fafafa)' : 'var(--c-ffffff)', ...(clickable ? { cursor: 'pointer' } : {}) }}>
                      <td style={S.td}>{p.name || p.email}{p.name && <div style={S.small}>{p.email}</div>}</td>
                      {manyClients && <td style={S.td}>{p.accountName}</td>}
                      <td style={S.td}>{p.role || '—'}</td>
                      {teams.length > 0 && <td style={S.td}>{p.teams.join(', ') || '—'}</td>}
                      <td style={S.num}>{p.viaPortal}</td>
                      <td style={S.num}>{p.viaEmail}</td>
                      <td style={{ ...S.num, fontWeight: 700 }}>{p.total}</td>
                      <td style={S.td}><span style={{ ...S.badge, background: bg, color }}>{p.statusText}</span></td>
                      <td style={S.when}>{p.lastSent ? formatDate(p.lastSent) : '—'}</td>
                      <td style={S.num}>{p.opened}</td>
                      <td style={S.when}>{p.lastSignIn ? formatDate(p.lastSignIn) : '—'}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
        <Pager {...pager} label="people" />
      </>}
    </div>
  );
}

const S = {
  filterBar: { background: 'var(--c-ffffff)', borderRadius: 12, padding: 16, marginBottom: 14, boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)' },
  row: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  input: { padding: '8px 10px', border: '1.5px solid var(--c-cfd8dc)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', background: 'var(--c-ffffff)', color: 'var(--t-333333)' },
  download: { padding: '8px 14px', background: 'var(--c-ffffff)', color: 'var(--t-00695c)', border: '1.5px solid var(--c-b2dfdb)', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  note: { margin: '12px 0 0', fontSize: 13, color: 'var(--t-546e7a)' },
  error: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13 },
  tiles: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10, marginBottom: 14 },
  tile: { background: 'var(--c-ffffff)', border: '1px solid var(--b-c8e6c9)', borderRadius: 12, padding: '10px 12px' },
  tileValue: { fontSize: 22, fontWeight: 800, color: 'var(--t-00695c)' },
  tileLabel: { fontSize: 11, color: 'var(--t-78909c)', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 2 },
  tileSub: { fontSize: 12, color: 'var(--t-90a4ae)', marginTop: 2 },
  trend: { background: 'var(--c-ffffff)', border: '1px solid var(--b-c8e6c9)', borderRadius: 12, padding: '12px 16px', marginBottom: 14 },
  trendHead: { display: 'flex', alignItems: 'baseline', fontSize: 13, color: 'var(--t-00695c)', gap: 10, flexWrap: 'wrap' },
  trendHint: { fontSize: 12, color: 'var(--t-90a4ae)' },
  dateLabel: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--t-546e7a)' },
  clear: { background: 'none', border: 'none', color: 'var(--t-00695c)', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  tableWrap: { background: 'var(--c-ffffff)', borderRadius: 12, overflow: 'auto', boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border: '1px solid var(--b-e8f5e9)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: 'var(--c-00695c)' },
  th: { padding: '12px 14px', textAlign: 'left', color: 'var(--t-ffffff)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  td: { padding: '10px 14px', fontSize: 13, color: 'var(--t-333333)', verticalAlign: 'top' },
  num: { padding: '10px 14px', fontSize: 13, color: 'var(--t-333333)', textAlign: 'center' },
  when: { padding: '10px 14px', fontSize: 12, color: 'var(--t-546e7a)', whiteSpace: 'nowrap' },
  small: { fontSize: 11, color: 'var(--t-78909c)' },
  badge: { padding: '3px 9px', borderRadius: 999, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' },
  empty: { padding: 30, textAlign: 'center' },
};
